/**
 * Zustand store for global app state
 */

import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { toast } from 'sonner';
import type { User } from '../../shared/types';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  logout: () => void;
  pendingVerificationEmail: string | null;
  setPendingVerificationEmail: (email: string | null) => void;

  // Theme - initialized from HTML script, hydration-safe
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  initializeTheme: () => void;

  // UI State
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Notifications
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Filters
  matchStageFilter: string | null;
  matchStatusFilter: string | null;
  setMatchStageFilter: (stage: string | null) => void;
  setMatchStatusFilter: (status: string | null) => void;

  // Selected pool (for context)
  selectedPoolSlug: string | null;
  setSelectedPoolSlug: (slug: string | null) => void;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

// Theme constants
const THEME_KEY = 'bolao2026-theme';
const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

function getPersistentStorage(): StateStorage {
  if (typeof localStorage !== 'undefined') return localStorage;
  return noopStorage;
}

// Store references to theme media query and listener for proper cleanup
let themeMediaQuery: MediaQueryList | null = null;
let themeMediaQueryListener: (() => void) | null = null;

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeToDOM(theme: Theme): ResolvedTheme {
  if (typeof document === 'undefined') return 'light';

  const root = document.documentElement;
  const resolved = theme === 'system' ? getSystemTheme() : theme;

  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  // Update meta theme-color
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', resolved === 'dark' ? '#171717' : '#009739');
  }

  return resolved;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Auth
      user: null,
      isAuthenticated: false,
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      logout: () => set({ user: null, isAuthenticated: false }),
      pendingVerificationEmail: null,
      setPendingVerificationEmail: (email) => set({ pendingVerificationEmail: email }),

      // Theme - safe defaults, real values set by initializeTheme
      theme: 'system',
      resolvedTheme: 'light',
      setTheme: (theme) => {
        localStorage.setItem(THEME_KEY, theme);
        const resolved = applyThemeToDOM(theme);
        set({ theme, resolvedTheme: resolved });
      },
      initializeTheme: () => {
        // Read from localStorage (already applied by HTML script)
        const stored = localStorage.getItem(THEME_KEY) as Theme | null;
        const theme: Theme = stored && ['light', 'dark', 'system'].includes(stored) ? stored : 'system';
        const resolved = theme === 'system' ? getSystemTheme() : theme;
        set({ theme, resolvedTheme: resolved });

        // Remove existing listener from stored MediaQueryList reference (prevents race condition)
        if (themeMediaQuery && themeMediaQueryListener) {
          themeMediaQuery.removeEventListener('change', themeMediaQueryListener);
          themeMediaQueryListener = null;
        }

        // Store MediaQueryList reference for proper cleanup
        themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        // Create new listener - use useAppStore.getState() to avoid stale closure
        themeMediaQueryListener = () => {
          const currentTheme = useAppStore.getState().theme;
          if (currentTheme === 'system') {
            const newResolved = applyThemeToDOM('system');
            useAppStore.setState({ resolvedTheme: newResolved });
          }
        };
        themeMediaQuery.addEventListener('change', themeMediaQueryListener);
      },

      // UI State
      sidebarOpen: false,
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      // Notifications — routes to sonner. The notifications list field is kept
      // empty and unused so existing store-shape consumers (tests, selectors)
      // do not break, but the Toaster mounted in App renders via sonner.
      notifications: [],
      addNotification: (notification) => {
        const opts = notification.duration !== undefined
          ? { duration: notification.duration }
          : undefined;
        switch (notification.type) {
          case 'success':
            toast.success(notification.message, opts);
            break;
          case 'error':
            toast.error(notification.message, opts);
            break;
          case 'warning':
            toast.warning(notification.message, opts);
            break;
          case 'info':
          default:
            toast.info(notification.message, opts);
            break;
        }
      },
      removeNotification: (id) => {
        toast.dismiss(id);
      },
      clearNotifications: () => {
        toast.dismiss();
      },

      // Filters
      matchStageFilter: null,
      matchStatusFilter: null,
      setMatchStageFilter: (stage) => set({ matchStageFilter: stage }),
      setMatchStatusFilter: (status) => set({ matchStatusFilter: status }),

      // Selected pool
      selectedPoolSlug: null,
      setSelectedPoolSlug: (slug) => set({ selectedPoolSlug: slug }),
    }),
    {
      name: 'bolao2026-storage',
      storage: createJSONStorage(getPersistentStorage),
      partialize: (state) => ({
        // Only persist these fields (theme persisted separately via THEME_KEY)
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        pendingVerificationEmail: state.pendingVerificationEmail,
        selectedPoolSlug: state.selectedPoolSlug,
      }),
    }
  )
);

// Selector hooks for better performance
export const useUser = () => useAppStore((state) => state.user);
export const useIsAuthenticated = () => useAppStore((state) => state.isAuthenticated);
export const useNotifications = () => useAppStore((state) => state.notifications);
export const useSelectedPool = () => useAppStore((state) => state.selectedPoolSlug);
export const usePendingVerificationEmail = () => useAppStore((state) => state.pendingVerificationEmail);

// Theme selectors
export const useTheme = () => useAppStore((state) => state.theme);
export const useResolvedTheme = () => useAppStore((state) => state.resolvedTheme);
export const useSetTheme = () => useAppStore((state) => state.setTheme);
export const useIsDark = () => useAppStore((state) => state.resolvedTheme === 'dark');
